import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  addRentalImagesComplete,
  AUTH_TOKEN,
  ConfirmDialogService,
  Destroyable,
  HttpGetRequestCacheService,
  isSomething,
  Rental,
  SaveForm,
  selectRentalById,
  untilDestroy,
  updateRentalBaseRateComplete,
  updateRentalComplete,
  updateRentalDetailsComplete,
  updateRentalInstructionsComplete,
} from '@tokeet-frontend/tv3-platform'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  GVRListingViewModel,
  CreateGVRListingSuccess,
  UpdateGVRListingSuccess,
  UpdateGVRListing,
  GVRChannelService,
  ListingValidationFieldResultItem,
  GVRListingPayload,
} from '@tokeet-frontend/channels'
import { Actions, ofType } from '@ngrx/effects'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, combineLatest, EMPTY, of } from 'rxjs'
import * as lodash from 'lodash'
import { catchError, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs/operators'
import { selectAllWebsites } from '@tv3/store/website/website.selectors'
import { WebsiteTypes } from '@tv3/store/website/website.model'
import { RentalValidationFixHelperService } from '../../rental-validation-fix-helper.service'
import { environment } from '@tv3/environments/environment'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { UpdateRoomComplete, UpsertRoomsComplete, DeleteRoomComplete } from '@tokeet-frontend/rentals'
import { WebsiteOverlayService } from '@tv3/containers/websites/overlays/website-overlay.service'
import { PublishWebsiteComplete } from '@tv3/store/website/website.actions'

@Component({
  selector: 'app-gvr-listing-details-overlay',
  templateUrl: './gvr-listing-details-overlay.component.html',
  styleUrls: ['./gvr-listing-details-overlay.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class GVRListingDetailsOverlayComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    cancellationPolicyType: [],
    websiteId: ['', [Validators.required]],
    websiteDomainId: [],
    websiteDisplayName: [],
    isCustomWebsite: [false],
    customListingUrl: [{ value: '', disabled: true }],
  })

  isAcmWebsiteSelected = false

  get storeLink() {
    let token = this.token$.value
    return `${environment.appStoreUrl}login?token=${token}&redirect=details/webready`
  }

  domains$ = new BehaviorSubject<{ websiteId: string; _id: string }[]>([])
  allWebreadyDomains: any[] = []

  provider: string = 'tokeet'
  errors: ListingValidationFieldResultItem[] = []

  isLoadingWebsites = true

  /** All website options before checkbox filtering — populated from store + webready API. */
  private allTokeetWebsiteOptions: { id: string; name: string; provide: string; websiteType: WebsiteTypes }[] = []
  private allWebreadyWebsiteOptions: { id: string; name: string; provide: string }[] = []

  websites$ = new BehaviorSubject<{ id: string; name: string; provide: string }[]>([])
  isDomainRequired = false
  isDomainDisabled = true
  rental: Rental

  isLoading = false

  constructor(
    private http: HttpClient,
    private store: Store<any>,
    private fb: FormBuilder,
    private actions: Actions,
    private confirm: ConfirmDialogService,
    private gvrService: GVRChannelService,
    private websiteOverlay: WebsiteOverlayService,
    private validationFixHelper: RentalValidationFixHelperService,
    private httpCache: HttpGetRequestCacheService,
    @Inject(AUTH_TOKEN) private token$: BehaviorSubject<string>,
    @Inject(MAT_DIALOG_DATA) public data: { listing: GVRListingViewModel },
    public dialogRef: MatDialogRef<GVRListingDetailsOverlayComponent>
  ) {
    super()
  }

  ngOnInit() {
    this.actions.pipe(ofType(CreateGVRListingSuccess, UpdateGVRListingSuccess), untilDestroy(this)).subscribe(() => {
      this.isLoading = false
      this.close()
    })

    this.actions
      .pipe(
        ofType(PublishWebsiteComplete),
        switchMap(() => this.validateRental()),
        untilDestroy(this)
      )
      .subscribe()

    this.store.pipe(select(selectRentalById(this.data.listing.rentalId)), untilDestroy(this)).subscribe((rental) => {
      this.rental = rental
    })

    // Build full website option lists, then apply checkbox filter
    combineLatest([
      this.store.pipe(select(selectAllWebsites)),
      this.httpCache
        .cacheGetRequest<any[]>(`@webready/private/websites`)
        .pipe(map((items) => lodash.filter(items, (t) => lodash.includes(t.rentals, this.data.listing.rentalId)))),
      this.httpCache.cacheGetRequest<any[]>(`@webready/private/domains`),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([tokeetWebsites, items, domains]) => {
        this.isLoadingWebsites = false
        this.allWebreadyDomains = domains

        // Keep both Website and BookingEngine types; filtering is done per checkbox state
        this.allTokeetWebsiteOptions = tokeetWebsites
          .filter((t) => t.type === WebsiteTypes.RentalWebsite || t.type === WebsiteTypes.BookingEngine)
          .filter((t) => lodash.includes(t.rentals, this.data.listing.rentalId))
          .map((t) => ({ id: t.id, name: t.title, provide: 'tokeet', websiteType: t.type as WebsiteTypes }))

        this.allWebreadyWebsiteOptions = items.map((t) => ({
          id: t._id as string,
          name: t.name as string,
          provide: 'webready',
        }))

        this.refreshWebsiteOptions()
      })

    // React to websiteId changes to update domain/ACM fields
    combineLatest([this.websites$.pipe(filter(isSomething)), this.form.get('websiteId').valueChanges])
      .pipe(untilDestroy(this))
      .subscribe(([websites, websiteId]) => {
        const websiteData = websites.find((t) => t.id === websiteId)
        const domainCtrl = this.form.get('websiteDomainId')
        const customListingUrlCtrl = this.form.get('customListingUrl')
        const isCustomWebsiteCtrl = this.form.get('isCustomWebsite')

        this.isAcmWebsiteSelected = websiteData?.provide === 'tokeet'

        if (websiteData?.provide === 'webready') {
          const wDomains = this.allWebreadyDomains.filter((t) => t.websiteId === websiteData.id)
          this.domains$.next(wDomains)
          domainCtrl.setValidators([Validators.required])
          domainCtrl.enable()
          domainCtrl.setValue(wDomains.find((t) => t._id === domainCtrl.value)?._id)
          this.isDomainRequired = true
          this.isDomainDisabled = false
        } else {
          domainCtrl.clearValidators()
          domainCtrl.disable()
          domainCtrl.reset()
          this.isDomainRequired = false
          this.isDomainDisabled = true
        }

        // customListingUrl is only active for tokeet provider + checkbox checked
        if (!this.isAcmWebsiteSelected) {
          customListingUrlCtrl.setValue('', { emitEvent: false })
          customListingUrlCtrl.disable({ emitEvent: false })
        } else {
          if (isCustomWebsiteCtrl.value) {
            customListingUrlCtrl.enable({ emitEvent: false })
          } else {
            customListingUrlCtrl.disable({ emitEvent: false })
          }
        }
      })

    // React to checkbox changes: refresh website list and toggle customListingUrl
    this.form
      .get('isCustomWebsite')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((isCustomWebsite) => {
        const customListingUrlCtrl = this.form.get('customListingUrl')

        // Refresh dropdown options based on new checkbox state
        this.refreshWebsiteOptions()

        // Clear selection if the current website is no longer in the new list
        const currentWebsiteId = this.form.get('websiteId').value
        const stillAvailable = this.websites$.value.some((t) => t.id === currentWebsiteId)
        if (!stillAvailable) {
          this.form.get('websiteId').setValue('', { emitEvent: false })
          this.isAcmWebsiteSelected = false
        }

        if (!this.isAcmWebsiteSelected) {
          customListingUrlCtrl.disable({ emitEvent: false })
          return
        }

        if (isCustomWebsite) {
          customListingUrlCtrl.enable({ emitEvent: false })
        } else {
          customListingUrlCtrl.setValue('', { emitEvent: false })
          customListingUrlCtrl.disable({ emitEvent: false })
        }
      })

    const rentalChange$ = this.actions.pipe(
      ofType(
        updateRentalComplete,
        updateRentalDetailsComplete,
        UpdateRoomComplete,
        UpsertRoomsComplete,
        addRentalImagesComplete,
        DeleteRoomComplete,
        updateRentalInstructionsComplete,
        updateRentalBaseRateComplete
      )
    )

    combineLatest([this.form.valueChanges, rentalChange$.pipe(startWith(true))])
      .pipe(
        debounceTime(500),
        switchMap(([v]) => {
          const { websiteId, websiteDomainId } = v
          const websiteData = this.websites$.value.find((t) => t.id === websiteId)
          if (websiteData?.provide === 'tokeet' && websiteId) {
            return this.validateRental()
          } else if (websiteData?.provide === 'webready' && websiteId && websiteDomainId) {
            return this.validateRental()
          }
          return of([])
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.form.patchValue({ ...this.data.listing })
  }

  /**
   * Rebuild websites$ based on the isCustomWebsite checkbox:
   * - Checked:   Tokeet RentalWebsite + BookingEngine types (no Webready)
   * - Unchecked: Tokeet RentalWebsite + Webready
   */
  private refreshWebsiteOptions() {
    const isCustomWebsite = this.form.get('isCustomWebsite').value

    if (isCustomWebsite) {
      // Custom website mode: all Tokeet website types (RentalWebsite + BookingEngine), no Webready
      this.websites$.next(
        this.allTokeetWebsiteOptions.filter(
          (t) => t.websiteType === WebsiteTypes.RentalWebsite || t.websiteType === WebsiteTypes.BookingEngine
        )
      )
    } else {
      this.websites$.next([
        ...this.allTokeetWebsiteOptions.filter((t) => t.websiteType === WebsiteTypes.RentalWebsite),
        ...this.allWebreadyWebsiteOptions,
      ])
    }
  }

  validateRental() {
    const { websiteId, websiteDomainId, isCustomWebsite, customListingUrl } = this.form.getRawValue()
    const websiteData = this.websites$.value.find((t) => t.id === websiteId)

    const payload: Omit<GVRListingPayload, 'cancellationPolicyType'> = {
      websiteId,
      websiteDomainId,
      websiteProvider: websiteData.provide,
      rentalId: this.data.listing.rentalId,
    }

    if (websiteData?.provide === 'tokeet') {
      payload.isCustomWebsite = !!isCustomWebsite
      payload.customListingUrl = isCustomWebsite ? (customListingUrl || '').trim() || undefined : undefined
    }

    return this.gvrService.getRentalValidation(payload).pipe(
      tap((errors) => {
        this.errors = errors
        this.provider = payload.websiteProvider
      }),
      catchError((error) => {
        this.store.dispatch(ActionFailed({ error }))
        return EMPTY
      })
    )
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const {
      cancellationPolicyType,
      websiteId,
      websiteDomainId,
      websiteDisplayName,
      isCustomWebsite,
      customListingUrl,
    } = form.getRawValue()
    const websiteData = this.websites$.value.find((t) => t.id === websiteId)

    const payload: GVRListingPayload = {
      websiteId,
      websiteDomainId,
      websiteProvider: websiteData.provide,
      websiteDisplayName,
      rentalId: this.data.listing.rentalId,
      cancellationPolicyType,
    }

    if (websiteData?.provide === 'tokeet') {
      payload.isCustomWebsite = !!isCustomWebsite
      payload.customListingUrl = isCustomWebsite ? (customListingUrl || '').trim() || undefined : undefined
    }

    this.isLoading = true
    this.validateRental().subscribe((errors) => {
      errors = lodash.filter(errors, (t) => t.type === 'error')
      if (errors.length) {
        this.isLoading = false
        return
      }

      this.store.dispatch(UpdateGVRListing({ listing: payload }))
    })
  }

  onFixRental(field: ListingValidationFieldResultItem) {
    if (this.provider === 'webready') {
      this.confirm
        .confirm({
          title: $localize`Fix rental data?`,
          body: $localize`Please go to the Webready app to fix this issue, because you're trying to link a Webready website with this listing.`,
          confirmText: $localize`Open Webready`,
        })
        .subscribe(() => {
          window.open(`${environment.webreadyUrl}login?token=${this.token$.value}`, '_blank')
        })
    } else {
      this.validationFixHelper.onFixRental(this.rental, field, this.errors, () => {
        if (field.field === 'website') {
          if (field.code === 'website-unpublished') {
            this.onPublishWebsite()
          }
        }
      })
    }
  }

  onCheckWebsiteData() {
    this.validateRental().subscribe({
      error: (error) => {
        this.store.dispatch(ActionFailed({ error }))
      },
    })
  }

  onPublishWebsite() {
    const { websiteId } = this.form.getRawValue()
    const websiteData = this.websites$.value.find((t) => t.id === websiteId)
    if (!websiteData) {
      return
    }

    if (websiteData.provide === 'webready') {
      window.open(`${environment.webreadyUrl}login?token=${this.token$.value}`, '_blank')
    } else {
      this.websiteOverlay.openSide(websiteId)
    }
  }

  close() {
    this.dialogRef.close()
  }
}
